import { createSelector } from '@reduxjs/toolkit';
import { ELevelResult } from 'Shared/types';
import { AssociationsService } from 'PhenotypeView/services';
import { getChromosomes } from 'Store/cacheData/selectors/selectors';
import { HIGH_IMPACT_LEVELS_BY_TYPE, MODERATE_IMPACT_LEVELS_BY_TYPE, MODIFIER_IMPACT_LEVELS_BY_TYPE, } from 'Shared/components/ImpactDescriptions/consts';
import { getValidatedPlotData } from 'Common/utils';
import { getPhenotype, getStats, getTraitsType } from '../../common';
import { getState } from '../../rootState';
export const getLevelResultState = createSelector(getState, (state) => state[ELevelResult.Variant]);
export const getAssociations = createSelector(getLevelResultState, ({ associations }) => associations);
export const getTableSettings = createSelector(getLevelResultState, ({ tableSettings }) => tableSettings);
export const getFilteredAssociationsIds = createSelector(getLevelResultState, ({ filteredAssociationsIds }) => filteredAssociationsIds);
export const getIsFiltering = createSelector(getLevelResultState, ({ isFiltering }) => isFiltering);
export const getFilters = createSelector(getLevelResultState, ({ filters }) => filters);
export const getTableData = createSelector(getAssociations, getFilteredAssociationsIds, ({ collection }, filteredAssociationsIds) => AssociationsService.getTableData(collection, filteredAssociationsIds));
export const getAllTableData = createSelector(getAssociations, ({ collection, order }) => AssociationsService.getTableData(collection, order));
export const getGenes = createSelector(getLevelResultState, ({ genes }) => genes);
export const getGenesNames = createSelector(getGenes, (genes) => Object.keys(genes));
export const getVariants = createSelector(getLevelResultState, ({ variants }) => variants);
export const getVariantsNames = createSelector(getVariants, (variants) => Object.keys(variants));
export const getCollapsingModels = createSelector(getLevelResultState, ({ collapsingModels }) => collapsingModels);
export const getIsMostSignificantModel = createSelector(getFilters, ({ isMostSignificantModel }) => isMostSignificantModel);
export const getPlotData = createSelector(getChromosomes, getTableData, (chromosomes, filteredAssociations) => AssociationsService.getPlotDataVLR(chromosomes, getValidatedPlotData(filteredAssociations)));
export const getAllPlotData = createSelector(getChromosomes, getAllTableData, (chromosomes, associations) => AssociationsService.getPlotDataVLR(chromosomes, getValidatedPlotData(associations)));
export const getConsequenceTypes = createSelector(getStats, (stats) => { var _a; return (_a = stats === null || stats === void 0 ? void 0 : stats.consequenceTypes) !== null && _a !== void 0 ? _a : []; });
export const getConsequenceTypeFilterHierarchy = createSelector(getConsequenceTypes, (consequenceTypes) => {
    const consequenceTypeFilter = consequenceTypes.map((str) => ({
        id: str,
        label: str,
    }));
    const consequenceTypeCategories = consequenceTypeFilter.reduce((categories, consequenceType) => {
        if (consequenceType.id in HIGH_IMPACT_LEVELS_BY_TYPE) {
            categories.highImpactChildren.push(consequenceType);
        }
        else if (consequenceType.id in MODERATE_IMPACT_LEVELS_BY_TYPE) {
            categories.moderateImpactChildren.push(consequenceType);
        }
        else if (consequenceType.id in MODIFIER_IMPACT_LEVELS_BY_TYPE) {
            categories.modifierImpactChildren.push(consequenceType);
        }
        else {
            categories.lowImpactChildren.push(consequenceType);
        }
        return categories;
    }, {
        lowImpactChildren: [],
        highImpactChildren: [],
        moderateImpactChildren: [],
        modifierImpactChildren: [],
    });
    return [
        {
            id: 'High Impact',
            label: 'High Impact',
            children: consequenceTypeCategories.highImpactChildren,
        },
        {
            id: 'Moderate Impact',
            label: 'Moderate Impact',
            children: consequenceTypeCategories.moderateImpactChildren,
        },
        {
            id: 'Modifier Impact',
            label: 'Modifier Impact',
            children: consequenceTypeCategories.modifierImpactChildren,
        },
        {
            id: 'Low Impact',
            label: 'Low Impact',
            children: consequenceTypeCategories.lowImpactChildren,
        },
    ];
});
export const getExportedData = createSelector(getTraitsType, getPhenotype, getTableData, getAllTableData, getPlotData, getAllPlotData, (traitsType, phenotype, tableData, allTableData, plotData, allPlotData) => ({
    traitsType,
    phenotype,
    tableData,
    allTableData,
    plotData,
    allPlotData,
}));
export const getAncestries = createSelector(getLevelResultState, ({ ancestries }) => ancestries);
export const getFiltersData = createSelector(getFilters, getGenesNames, getVariantsNames, getCollapsingModels, getConsequenceTypes, getAncestries, (filters, genes, variants, collapsingModels, consequenceTypes, ancestries) => ({
    filters,
    genes,
    variants,
    collapsingModels,
    consequenceTypes,
    ancestries,
}));
export default {
    getAncestries,
    getAssociations,
    getTableSettings,
    getFilteredAssociationsIds,
    getIsFiltering,
    getFilters,
    getTableData,
    getAllTableData,
    getCollapsingModels,
    getGenes,
    getVariants,
    getPlotData,
    getAllPlotData,
    getExportedData,
    getFiltersData,
};
